import { useState, useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { ThemeProvider } from './components/providers/ThemeProvider'
import Navigation from './components/Navigation'
import Footer from './components/Footer'
import ScrollToTop from './components/ScrollToTop'
import BreadcrumbSchema from './components/seo/BreadcrumbSchema'
import SiteNavigationSchema from './components/seo/SiteNavigationSchema'
import { Helmet } from 'react-helmet-async'
import { API_BASE } from './config/api'
import './App.css'

function getTitleForPath(path: string): string {
  const base = 'Ionic Errrrs Code'
  if (path === '/privacy') return `Privacy Policy | ${base}`
  if (path === '/terms') return `Terms of Service | ${base}`
  if (path === '/about') return `About | ${base}`
  if (path === '/projects') return `Projects | ${base}`
  if (path === '/team') return `Team | ${base}`
  if (path === '/technologies') return `Technologies | ${base}`
  if (path === '/contact') return `Contact | ${base}`
  if (path === '/start-project') return `Start a Project | ${base}`
  if (path === '/') return `${base} — We craft digital experiences`
  return base
}

function Layout() {
  const location = useLocation()
  const title = getTitleForPath(location.pathname)
  const [whatsappPhone, setWhatsappPhone] = useState('92360923617')

  useEffect(() => {
    const fetchContact = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/v1/settings/contact`)
        if (res.ok) {
          const json = await res.json()
          if (json.success && json.data?.phone) {
            const cleaned = json.data.phone.replace(/[^0-9]/g, '')
            if (cleaned) {
              setWhatsappPhone(cleaned)
            }
          }
        }
      } catch (e) {
        console.error('Failed to fetch contact settings for WhatsApp link:', e)
      }
    }
    fetchContact()
  }, [])

  return (
    <ThemeProvider>
      <div className="App" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Helmet>
          <title>{title}</title>
            <meta name="description" content="Ionic Errrrs Code is a development team building high-performance mobile apps, web apps, APIs and backends." />
            <link rel="canonical" href={`https://ionicerrrrscode.com${location.pathname}`} />
            
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content="Ionic Errrrs Code" />
            <meta property="og:title" content={title} />
            <meta property="og:description" content="Ionic Errrrs Code is a development team building high-performance mobile apps, web apps, APIs and backends." />
            <meta property="og:url" content={`https://ionicerrrrscode.com${location.pathname}`} />
            
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content="Ionic Errrrs Code is a development team building high-performance mobile apps, web apps, APIs and backends." />
          </Helmet>
          <BreadcrumbSchema />
          <SiteNavigationSchema />
          <ScrollToTop />
          <Navigation />
          <main style={{ flex: 1 }}>
            <div key={location.pathname}>
              <Outlet />
            </div>
          </main>
        <Footer />
        
        {/* Floating WhatsApp Button */}
        <a 
          href={`https://wa.me/${whatsappPhone}`} 
          target="_blank" 
          rel="noopener noreferrer"
          aria-label="Chat on WhatsApp"
          style={{
            position: 'fixed',
            bottom: '28px',
            right: '28px',
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            backgroundColor: '#22c55e',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(34, 197, 94, 0.35)',
            zIndex: 999,
            transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.3s ease',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.1) translateY(-2px)'
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(34, 197, 94, 0.5)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1) translateY(0)'
            e.currentTarget.style.boxShadow = '0 4px 16px rgba(34, 197, 94, 0.35)'
          }}
        >
          <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
            <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.514 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.717-1.458L0 24zm6.59-4.846c1.66.986 3.298 1.488 4.795 1.49 5.43-.004 9.85-4.43 9.854-9.885.002-2.618-1.01-5.07-2.855-6.917C16.536 1.997 14.09 1.01 11.5 1.01 6.13 1.01 1.75 5.39 1.745 10.76c-.001 1.62.43 3.2 1.25 4.594l-.993 3.626 3.73-.977c1.353.738 2.805 1.15 4.325 1.152zm10.744-7.405c-.29-.144-1.715-.845-1.98-.94-.264-.097-.456-.145-.648.143-.19.29-.74.94-.907 1.133-.166.19-.333.213-.623.07-.29-.143-1.223-.45-2.33-1.436-.86-.767-1.442-1.716-1.61-2.003-.17-.29-.017-.447.127-.59.13-.13.29-.335.434-.503.14-.17.19-.29.285-.483.097-.19.048-.36-.024-.504-.07-.143-.647-1.56-.888-2.138-.234-.564-.473-.487-.648-.496-.168-.008-.36-.01-.553-.01-.19 0-.5.07-.76.36-.26.29-1 .98-1 2.388s1.025 2.766 1.168 2.96c.143.19 2.017 3.08 4.885 4.316.683.294 1.217.47 1.63.6.688.22 1.314.19 1.808.116.55-.083 1.715-.7 1.96-1.378.24-.68.24-1.26.17-1.38-.072-.12-.263-.19-.553-.335z"/>
          </svg>
        </a>
      </div>
    </ThemeProvider>
  )
}

export default Layout
